import { Divider } from "@heroui/react";
import { IconToolTip } from "/app/src/components/icons/IconBuilder";
import { useStatusContext } from "/app/src/contexts/hooks/useStatusContext";
import "./_status.scss";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { warehouseService } from "/app/src/services";
import { useTimezoneContext } from "/app/src/contexts/hooks/useTimezoneContext";
import { useLicenseContext } from "/app/src/contexts/hooks/useLicenseContext";
import Box from "/app/src/components/generic/components/box";
import { useEffect, useRef, useState } from "react";
import moment from "moment";
import { useConnections } from "../../contexts/ConnectionsStatusContext";

/**
 * Component for displaying general info
 * @returns JSX.Element
 *
 */
export default function GeneralInfo() {
  const { version, warehouseLimit, releaseDate } = useStatusContext();
  const { powerPickVersion } = useLicenseContext();
  const { userTimeZone, globalTimeZone } = useTimezoneContext();
  const { powerPick } = useConnections();
  const { t } = useTranslation();
  const [currentTime, setCurrentTime] = useState<string>("");
  const animationFrameId = useRef(null);

  useEffect(() => {
    if (globalTimeZone !== null) {
      /**
       * Updates the current time every animation frame based on the specified time zone.
       * @returns {void}
       */
      const animationFrameCallback = () => {
        setCurrentTime(moment().tz(globalTimeZone).format("hh:mm:ss A"));
        //updates the animationFrameId with the new requestAnimationFrame
        animationFrameId.current = requestAnimationFrame(
          animationFrameCallback,
        );
      };
      // Starts the animation frame
      animationFrameId.current = requestAnimationFrame(animationFrameCallback);

      //cleanup when the component unmounts or the globalTimeZone changes
      return () => {
        if (animationFrameId.current) {
          cancelAnimationFrame(animationFrameId.current);
        }
      };
    }
    return null;
  }, [currentTime, globalTimeZone]);

  const { data: warehouseCount } = useQuery({
    queryKey: ["warehouseCount"],
    queryFn: () => {
      return warehouseService.getCount();
    },
    initialData: { count: 0 },
    select: (data: { count: number }) => {
      return data.count;
    },
  });

  const generalInfoData = [
    {
      title: t("translation:power_pick_version"),
      value: !powerPick?.status ? (
        <IconToolTip
          content={t("translation:no_power_pick_connection")}
          icon="Warning"
          size={20}
          color="#d0021b"
        />
      ) : (
        <div>{powerPickVersion}</div>
      ),
    },
    {
      title: t("translation:power_pick_warehouses"),
      value: !powerPick?.status ? (
        <IconToolTip
          content={t("translation:no_power_pick_connection")}
          icon="Warning"
          size={20}
          color="#d0021b"
        />
      ) : (
        <div>
          {warehouseCount} ({warehouseLimit} {t("translation:licensed")})
        </div>
      ),
    },
    {
      title: t("translation:itempath_version"),
      value: version,
    },
    {
      title: t("translation:release_date"),
      value: releaseDate,
    },
    {
      title: t("translation:User_Time_Zone"),
      value:
        userTimeZone === "" ? (
          t("translation:time_zone_not_set")
        ) : (
          <div>{userTimeZone}</div>
        ),
    },
    {
      title: t("translation:Global_Time_Zone"),
      value:
        globalTimeZone === "" ? (
          t("translation:time_zone_not_set")
        ) : (
          <div>{globalTimeZone}</div>
        ),
    },
    { title: t("translation:current_time"), value: currentTime },
  ];

  return (
    <Box>
      <h2 className="text-neutral-charcoal font-archivo text-medium font-medium mb-0">
        {t("translation:general_info")}
      </h2>
      <Divider className="my-3" />
      {generalInfoData.map((info) => (
        <>
          <div
            className="flex justify-between items-center mb-4"
            key={info.title}
          >
            <h3 className="font-archivo text-neutral-charcoal text-small font-medium mb-0">
              {info.title}
            </h3>
            <h3 className="font-archivo text-neutral-smoke text-small font-medium mb-0">
              {info.value}
            </h3>
          </div>
          <Divider className="my-3" />
        </>
      ))}
    </Box>
  );
}
